import type { PcsTableRow } from "../types/encoder";

export type PcsSelection = Record<number, string | null>;

export type PcsFilterResult = {
  validRows: PcsTableRow[];
  available: Record<number, string[]>;
  labels: Record<number, Record<string, string>>;
  matchingTables: number;
  matchingRows: number;
  validCodesRemaining: number;
};

/** Default Med/Surg axis titles (section 0 / unset). */
export const PCS_DEFAULT_AXIS_TITLES = [
  'Section',
  'Body System',
  'Operation',
  'Body Part',
  'Approach',
  'Device',
  'Qualifier',
] as const;

/**
 * CMS ICD-10-PCS table axis titles by section (chars 1–7).
 * Sourced from icd10pcs_tables XML `<axis><title>` values.
 */
export const PCS_SECTION_AXIS_TITLES: Record<string, readonly string[]> = {
  '0': PCS_DEFAULT_AXIS_TITLES,
  '1': PCS_DEFAULT_AXIS_TITLES,
  '2': [
    'Section',
    'Body System',
    'Operation',
    'Body Region',
    'Approach',
    'Device',
    'Qualifier',
  ],
  '3': [
    'Section',
    'Body System',
    'Operation',
    'Body System / Region',
    'Approach',
    'Substance',
    'Qualifier',
  ],
  '4': [
    'Section',
    'Body System',
    'Operation',
    'Body System',
    'Approach',
    'Function / Device',
    'Qualifier',
  ],
  '5': [
    'Section',
    'Body System',
    'Operation',
    'Body System',
    'Duration',
    'Function',
    'Qualifier',
  ],
  '6': [
    'Section',
    'Body System',
    'Operation',
    'Body System',
    'Duration',
    'Qualifier',
    'Qualifier',
  ],
  '7': [
    'Section',
    'Body System',
    'Operation',
    'Body Region',
    'Approach',
    'Method',
    'Qualifier',
  ],
  '8': [
    'Section',
    'Body System',
    'Operation',
    'Body Region',
    'Approach',
    'Method',
    'Qualifier',
  ],
  '9': [
    'Section',
    'Body System',
    'Operation',
    'Body Region',
    'Approach',
    'Method',
    'Qualifier',
  ],
  B: [
    'Section',
    'Body System',
    'Type',
    'Body Part',
    'Contrast',
    'Qualifier',
    'Qualifier',
  ],
  C: [
    'Section',
    'Body System',
    'Type',
    'Body Part',
    'Radionuclide',
    'Qualifier',
    'Qualifier',
  ],
  D: [
    'Section',
    'Body System',
    'Modality',
    'Treatment Site',
    'Modality Qualifier',
    'Isotope',
    'Qualifier',
  ],
  F: [
    'Section',
    'Section Qualifier',
    'Type',
    'Body System / Region',
    'Type Qualifier',
    'Equipment',
    'Qualifier',
  ],
  G: [
    'Section',
    'Body System',
    'Type',
    'Qualifier',
    'Qualifier',
    'Qualifier',
    'Qualifier',
  ],
  H: [
    'Section',
    'Body System',
    'Type',
    'Qualifier',
    'Qualifier',
    'Qualifier',
    'Qualifier',
  ],
  X: [
    'Section',
    'Body System',
    'Operation',
    'Body Part',
    'Approach',
    'Device / Substance / Technology',
    'Qualifier',
  ],
};

export function getPcsAxisTitles(
  sectionCode: string | null | undefined,
): readonly string[] {
  if (!sectionCode) return PCS_DEFAULT_AXIS_TITLES;
  return PCS_SECTION_AXIS_TITLES[sectionCode] ?? PCS_DEFAULT_AXIS_TITLES;
}

/** Numbered column headers for the 7-character PCS grid. */
export function getPcsPositionHeaders(
  sectionCode: string | null | undefined,
): string[] {
  return getPcsAxisTitles(sectionCode).map(
    (title, index) => `${index + 1}. ${title}`,
  );
}

export type PcsKeyAxisKind = 'bodyPart' | 'device' | 'substance';

/** PCS reference keys whose synonyms live in definition `includes`. */
export function getPcsKeyAxisKind(axisTitle: string): PcsKeyAxisKind | null {
  const title = axisTitle.trim().toLowerCase();
  if (title === 'body part' || title === 'body region') return 'bodyPart';
  if (title === 'substance') return 'substance';
  if (
    title === 'device' ||
    title === 'function / device' ||
    title === 'equipment' ||
    title === 'device / substance / technology'
  ) {
    return 'device';
  }
  return null;
}

export function getPcsKeySectionLabel(
  kind: PcsKeyAxisKind,
  axisTitle: string,
): string {
  if (kind === 'device' && axisTitle.toLowerCase().includes('substance')) {
    return 'Device / Substance Key';
  }
  switch (kind) {
    case 'bodyPart':
      return 'Body Part Key';
    case 'device':
      return 'Device Key';
    case 'substance':
      return 'Substance Key';
  }
}

export const PCS_POSITION_HEADERS = getPcsPositionHeaders(null);

export const emptySelection = (): PcsSelection => ({
  1: null,
  2: null,
  3: null,
  4: null,
  5: null,
  6: null,
  7: null,
});

export function pcsSelectionsEqual(
  left: PcsSelection,
  right: PcsSelection,
): boolean {
  for (let pos = 1; pos <= 7; pos++) {
    if ((left[pos] ?? null) !== (right[pos] ?? null)) return false;
  }
  return true;
}

/** True when `char` can appear at `pos` given the other selected characters. */
export function isPcsCharValidAtPosition(
  rows: PcsTableRow[],
  selection: PcsSelection,
  pos: number,
  char: string,
): boolean {
  const trial: PcsSelection = { ...selection, [pos]: char };
  return rows.some((row) => rowMatchesSelection(row, trial));
}

/**
 * Drop only characters that cannot be combined with already-accepted ones.
 * An invalid digit must not wipe the rest of a valid code.
 */
export function pruneIncompatiblePcsSelection(
  rows: PcsTableRow[],
  selection: PcsSelection,
): PcsSelection {
  const kept = emptySelection();
  for (let pos = 1; pos <= 7; pos++) {
    const code = selection[pos];
    if (!code) continue;
    const trial: PcsSelection = { ...kept, [pos]: code };
    if (rows.some((row) => rowMatchesSelection(row, trial))) {
      kept[pos] = code;
    }
  }
  return kept;
}

/**
 * Fill empty positions that have exactly one valid option.
 * Runs left-to-right until stable so cascades (e.g. Body System → Approach) resolve.
 */
export function fillSolePcsOptions(
  rows: PcsTableRow[],
  selection: PcsSelection,
): PcsSelection {
  let next: PcsSelection = { ...selection };
  for (let pass = 0; pass < 7; pass++) {
    const { available } = applyPcsFilters(rows, next);
    let filled = false;
    for (let pos = 1; pos <= 7; pos++) {
      if (next[pos]) continue;
      const codes = available[pos] ?? [];
      if (codes.length !== 1) continue;
      const only = codes[0];
      if (!only) continue;
      next = { ...next, [pos]: only };
      filled = true;
    }
    if (!filled) break;
  }
  return next;
}

export function rowMatchesSelection(
  row: PcsTableRow,
  selection: PcsSelection,
  excludePos?: number,
): boolean {
  for (let pos = 1; pos <= 7; pos++) {
    if (pos === excludePos) continue;
    const selected = selection[pos];
    if (!selected) continue;
    const axis = row[String(pos)];
    if (!axis || !(selected in axis)) return false;
  }
  return true;
}

export function applyPcsFilters(
  rows: PcsTableRow[],
  selection: PcsSelection,
): PcsFilterResult {
  // Rows matching the full current selection (for status / validity).
  const validRows = rows.filter((row) => rowMatchesSelection(row, selection));

  const available: Record<number, Set<string>> = {
    1: new Set(),
    2: new Set(),
    3: new Set(),
    4: new Set(),
    5: new Set(),
    6: new Set(),
    7: new Set(),
  };
  const labels: Record<number, Record<string, string>> = {
    1: {},
    2: {},
    3: {},
    4: {},
    5: {},
    6: {},
    7: {},
  };

  // For each column, options come from rows that match every OTHER
  // selection — so choosing an option does not hide siblings in that column.
  for (let pos = 1; pos <= 7; pos++) {
    const availableAtPos = available[pos];
    const labelsAtPos = labels[pos];
    if (!availableAtPos || !labelsAtPos) continue;

    for (const row of rows) {
      if (!rowMatchesSelection(row, selection, pos)) continue;
      const axis = row[String(pos)];
      if (!axis) continue;
      for (const [code, label] of Object.entries(axis)) {
        availableAtPos.add(code);
        labelsAtPos[code] = label;
      }
    }
  }

  const matchingTables = new Set<string>();
  let validCodesRemaining = 0;

  for (const row of validRows) {
    let tableKey = '';
    for (const pos of [1, 2, 3] as const) {
      const axis = row[String(pos)] ?? {};
      tableKey += Object.keys(axis).sort().join('');
    }
    matchingTables.add(tableKey);

    let combos = 1;
    for (let pos = 1; pos <= 7; pos++) {
      if (selection[pos]) {
        combos *= 1;
      } else {
        combos *= Object.keys(row[String(pos)] ?? {}).length || 1;
      }
    }
    validCodesRemaining += combos;
  }

  const availableSorted: Record<number, string[]> = {
    1: [],
    2: [],
    3: [],
    4: [],
    5: [],
    6: [],
    7: [],
  };
  for (let pos = 1; pos <= 7; pos++) {
    const codes = available[pos];
    availableSorted[pos] = codes
      ? Array.from(codes).sort((a, b) =>
          a.localeCompare(b, undefined, { numeric: true }),
        )
      : [];
  }

  return {
    validRows,
    available: availableSorted,
    labels,
    matchingTables: matchingTables.size,
    matchingRows: validRows.length,
    validCodesRemaining,
  };
}

export function buildCodeString(selection: PcsSelection): string {
  return [1, 2, 3, 4, 5, 6, 7].map((pos) => selection[pos] ?? '-').join('');
}

export function buildDescriptionPath(
  selection: PcsSelection,
  labels: Record<number, Record<string, string>>,
): string {
  return [1, 2, 3, 4, 5, 6, 7]
    .map((pos) => {
      const code = selection[pos];
      if (!code) return '-';
      return labels[pos]?.[code] ?? '?';
    })
    .join(' > ');
}

/** ROOT path of selected axis labels, skipping empty characters. */
export function buildSelectedLabelsPath(
  selection: PcsSelection,
  labels: Record<number, Record<string, string>>,
): string {
  const parts: string[] = [];
  for (let pos = 1; pos <= 7; pos += 1) {
    const code = selection[pos];
    if (!code) continue;
    parts.push(labels[pos]?.[code] || code);
  }
  return parts.join(' > ');
}

export function isPcsInspectorDefinitionAxis(axisTitle: string): boolean {
  const title = axisTitle.trim().toLowerCase();
  return (
    title === 'operation' ||
    title === 'approach' ||
    title === 'type' ||
    title === 'modality'
  );
}

export function selectionFromTargetCode(targetCode: string): PcsSelection {
  const next = emptySelection();
  for (let i = 0; i < Math.min(targetCode.length, 7); i++) {
    const char = targetCode[i];
    // Underscore / hyphen / question mark = intentionally unset position
    // (e.g. "0_C____" = section 0 + operation C, body system open).
    if (!char || char === '_' || char === '-' || char === '?') continue;
    if (PCS_CHAR_RE.test(char)) next[i + 1] = char.toUpperCase();
  }
  return next;
}

/** Valid ICD-10-PCS characters (I and O are excluded by CMS). */
const PCS_CHAR_RE = /^[0-9A-HJ-NP-Z]$/i;

export function isPcsCharacter(value: string): boolean {
  return value.length === 1 && PCS_CHAR_RE.test(value);
}

/**
 * Parse a pasted/typed value into a PCS code string (1–7 characters).
 * Accepts optional description suffixes like "0FT44ZZ - Excision…".
 */
export function parsePcsCodeInput(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  let compact = '';
  for (const ch of trimmed.toUpperCase()) {
    if (PCS_CHAR_RE.test(ch)) {
      compact += ch;
      if (compact.length === 7) break;
      continue;
    }
    // Skip separators commonly used in display / paste formatting.
    if (/[\s.\-_]/.test(ch)) continue;
    // Stop at the first non-code character (e.g. description text).
    break;
  }

  if (compact.length === 0) return null;
  return compact;
}

/**
 * Apply a definition (body part / device / substance / approach / operation)
 * onto the builder selection using matching table axis labels.
 */
export function selectionFromDefinition(
  rows: PcsTableRow[],
  def: { section: string; pos: number; title: string },
): PcsSelection {
  const next = emptySelection();
  next[1] = def.section;

  const titleLower = def.title.toLowerCase();
  const codesAtPos = new Set<string>();
  const bodySystems = new Set<string>();
  const operations = new Set<string>();

  for (const row of rows) {
    const sectionAxis = row['1'];
    if (!sectionAxis || !(def.section in sectionAxis)) continue;

    const axis = row[String(def.pos)];
    if (!axis) continue;

    let matchedCode: string | null = null;
    for (const [code, label] of Object.entries(axis)) {
      if (label.toLowerCase() === titleLower) {
        matchedCode = code;
        break;
      }
    }
    if (!matchedCode) continue;

    codesAtPos.add(matchedCode);

    const bodySystemCode = Object.keys(row['2'] ?? {})[0];
    if (bodySystemCode) bodySystems.add(bodySystemCode);

    const operationCode = Object.keys(row['3'] ?? {})[0];
    if (operationCode) operations.add(operationCode);
  }

  if (codesAtPos.size === 1) {
    next[def.pos] = Array.from(codesAtPos)[0] ?? null;
  }
  if (bodySystems.size === 1) {
    next[2] = Array.from(bodySystems)[0] ?? null;
  }
  if (operations.size === 1) {
    next[3] = Array.from(operations)[0] ?? null;
  }

  return next;
}

export const ROOT_OPERATION_GUIDELINES: Record<string, string[]> = {
  Bypass: [
    'B3.6a: Bypass identifies the body part bypassed FROM and the qualifier identifies the body part bypassed TO.',
    'B3.6b: Coronary artery bypass procedures are coded differently than other bypass procedures.',
    'B3.6c: If multiple coronary artery sites are bypassed, a separate procedure is coded for each coronary artery site that uses a different device and/or qualifier.',
  ],
  Control: [
    'B3.7: The root operation Control is used for procedures that attempt to stop postprocedural or other acute bleeding.',
  ],
  Excision: [
    'B3.4a: If an Excision procedure results in taking out a portion of a body part and the intent is biopsy, both the Excision and the diagnostic qualifier are coded.',
    'B3.4b: Excision for graft requires coding both the excision and the placement of the graft when both are distinct objectives.',
  ],
  Resection: [
    'B3.8: Resection is coded when the entire body part is cut out or off without replacement; Excision is coded when only a portion is removed.',
  ],
  Extirpation: [
    'B3.4c: Extirpation is taking or cutting out solid matter from a body part (e.g., thrombectomy, embolectomy, excision of a foreign body).',
    'The solid matter may be an abnormal byproduct of a biological function or a foreign body; it may be imbedded in a body part or in the lumen of a tubular body part.',
  ],
  Extraction: [
    'B3.4a: Extraction is pulling or stripping out or off all or a portion of a body part by the use of force.',
  ],
  Drainage: [
    'B3.6a (Drainage): Drainage is taking or letting out fluids and/or gases from a body part; a drainage device may be left in place.',
  ],
  Fragmentation: [
    'B3.3: Fragmentation is breaking solid matter in a body part into pieces; the pieces remain in the body.',
  ],
  Destruction: [
    'B3.2: Destruction is physical eradication of all or a portion of a body part by the direct use of energy, force, or a destructive agent.',
  ],
  Dilation: [
    'Dilation is expanding an orifice or the lumen of a tubular body part; the diameter is increased without cutting.',
  ],
  Restriction: [
    'Restriction is partially closing an orifice or the lumen of a tubular body part.',
  ],
  Occlusion: [
    'B3.12: If the objective of an embolization procedure is to completely close a vessel, the root operation Occlusion is coded. If the objective is to narrow the lumen, Restriction is coded.',
  ],
  Insertion: [
    'Insertion is putting in a nonbiological appliance that monitors, assists, performs, or prevents a physiological function but does not physically take the place of a body part.',
  ],
  Supplement: [
    'Supplement is putting in or on biological or synthetic material that physically reinforces and/or augments the function of a portion of a body part.',
  ],
  Change: [
    'B3.9: Change is taking out or off a device from a body part and putting back an identical or similar device without cutting or puncturing the skin or mucous membrane.',
  ],
  Removal: [
    'Removal is taking out or off a device from a body part; if a device is taken out and a similar device put in without cutting or puncturing skin/mucous membrane, code Change.',
  ],
  Revision: [
    'Revision is correcting a malfunctioning or displaced device by taking out and/or putting back components of the device without taking out the whole device.',
  ],
  Fusion: [
    'B3.10a: The body part coded for a spinal vertebral joint(s) rendered immobile by a spinal fusion procedure is classified by the level of the spine.',
    'B3.10b: If multiple vertebral joints are fused, a separate procedure is coded for each vertebral joint that uses a different device and/or qualifier.',
    'B3.10c: Combinations of devices and materials are often used on a vertebral joint to render the joint immobile.',
  ],
  Inspection: [
    'B3.11a: Inspection of a body part(s) performed in order to achieve the objective of a procedure is not coded separately.',
    'B3.11b: If multiple tubular body parts are inspected, the most distal body part is coded.',
    'B3.11c: When both an Inspection procedure and another procedure are performed on the same body part during the same episode, if the Inspection procedure is performed using a different approach than the other procedure, the Inspection procedure is coded separately.',
  ],
  Release: [
    'B3.13: In the root operation Release, the body part value coded is the body part being freed and not the tissue being manipulated or cut to free the body part.',
    'B3.14: If the sole objective of the procedure is freeing a body part without cutting the body part, the root operation is Release.',
  ],
  Reposition: [
    'B3.15: Reduction of a displaced fracture is coded to the root operation Reposition and the application of a cast or splint in conjunction with the Reposition procedure is not coded separately.',
  ],
  Transplantation: [
    'B3.16: Putting in a mature and functioning living body part taken from another individual or animal is coded to the root operation Transplantation.',
  ],
  Transfer: [
    'B3.17: The root operation Transfer contains qualifiers that can be used to specify when a transfer flap is composed of more than one tissue layer, such as a musculocutaneous flap.',
  ],
  Replacement: [
    'B3.18: If an excision or resection of a body part is followed by a replacement procedure, code both procedures to identify each distinct objective, except when the excision or resection is considered integral and preparatory for the replacement procedure.',
  ],
  Detachment: [
    'B3.19: The root operation Detachment contains qualifiers that can be used to specify the level where the extremity was amputated.',
  ],
  Repair: [
    'Repair is restoring a body part to its normal anatomic structure and function; used only when the method to accomplish the repair is not one of the other root operations.',
  ],
  Map: [
    'Map is locating the route of passage of electrical impulses and/or locating functional areas in a body part.',
  ],
  Division: [
    'Division is cutting into a body part without draining fluids and/or gases in order to separate or transect a body part.',
  ],
  Creation: [
    'Creation is putting in or on biological or synthetic material to form a new body part that to some extent replicates the anatomic structure or function of an absent body part.',
  ],
};

export function getGuidelinesForOperation(
  operationLabel: string,
  operationDefinition?: {
    title: string;
    definition: string;
    explanation: string;
  } | null,
): string[] {
  const lines: string[] = [];

  if (operationLabel) {
    lines.push(`Selected root operation: ${operationLabel}`);
  }

  if (operationDefinition?.definition) {
    lines.push(`Definition: ${operationDefinition.definition}`);
  }
  if (operationDefinition?.explanation) {
    lines.push(`Explanation: ${operationDefinition.explanation}`);
  }

  lines.push(
    'A6: The purpose of the Alphabetic Index is to locate the appropriate table that contains all information necessary to construct a procedure code. The PCS Tables should always be consulted to find the most appropriate valid code.',
    'A8: All seven characters must be specified to be a valid code. If the documentation is incomplete for coding purposes, the physician should be queried for the necessary information.',
    'A9: Within a PCS table, valid codes include all combinations of choices in characters 4 through 7 contained in the same row of the table.',
  );

  if (!operationLabel) return lines;

  const op = operationLabel.toLowerCase();
  let matched = false;
  for (const [key, glList] of Object.entries(ROOT_OPERATION_GUIDELINES)) {
    if (op.includes(key.toLowerCase())) {
      lines.push(...glList);
      matched = true;
    }
  }

  if (!matched) {
    lines.push(
      'General Guidelines:',
      '- Code to the highest level of specificity.',
      '- Review all inclusion/exclusion notes in the tabular list.',
      '- Ensure the approach and device match the clinical documentation.',
    );
  }

  return lines;
}

export type ParsedGuidelineLine = {
  label: string | null;
  text: string;
};

const GUIDELINE_SECTION_RE =
  /^([A-Z]\d+(?:\.\d+[a-z]?)?)(?:\s+\(([^)]+)\))?:\s*(.*)$/i;

/**
 * Split a guideline line into a label + body so the UI can style them apart.
 * Official convention numbers (A6, B3.6a) are shown as "Section …".
 */
export function parseGuidelineLine(line: string): ParsedGuidelineLine {
  const sectionMatch = line.match(GUIDELINE_SECTION_RE);
  if (sectionMatch?.[1]) {
    const code = sectionMatch[1].toUpperCase();
    const note = sectionMatch[2]?.trim();
    const text = (sectionMatch[3] ?? '').trim();
    return {
      label: note ? `Section ${code} (${note})` : `Section ${code}`,
      text,
    };
  }

  const labeled = line.match(/^([^:]{2,48}):\s*(.*)$/);
  if (labeled?.[1]) {
    return {
      label: labeled[1].trim(),
      text: (labeled[2] ?? '').trim(),
    };
  }

  return { label: null, text: line };
}

/** Normalize a PCS axis label into an AHA keyword search string. */
export function normalizeAhaSearchKeyword(label: string): string {
  return label
    .replace(/[/,]/g, ' ')
    .replace(/\b(left|right|bilateral)\b/gi, ' ')
    .replace(/[^a-zA-Z0-9\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const PCS_AHA_SKIP_LABELS: Record<string, true> = {
  'no qualifier': true,
  'none': true,
  'unspecified': true,
  'no device': true,
  'no supplement': true,
  'other': true,
  'external': true,
  'open': true,
  'percutaneous': true,
  'percutaneous endoscopic': true,
  'via natural or artificial opening': true,
  'via natural or artificial opening endoscopic': true,
};

/**
 * AHA keywords from the current PCS axis selections.
 * Body part / device / operation first; generic approach/qualifier values are skipped.
 */
export function collectPcsAhaKeywords(
  selection: PcsSelection,
  labels: Record<number, Record<string, string>>,
  extra: string[] = [],
): string[] {
  const priorityPositions = [4, 6, 3, 2, 5, 7] as const;
  const seen: Record<string, true> = {};
  const out: string[] = [];
  const push = (raw: string) => {
    const normalized = normalizeAhaSearchKeyword(raw);
    const key = normalized.toLowerCase();
    if (!normalized || seen[key] || PCS_AHA_SKIP_LABELS[key]) return;
    seen[key] = true;
    out.push(normalized);
  };
  for (let i = 0; i < priorityPositions.length; i += 1) {
    const pos = priorityPositions[i];
    const code = selection[pos];
    if (!code) continue;
    const label = labels[pos]?.[code];
    if (label) push(label);
  }
  for (let i = 0; i < extra.length; i += 1) {
    if (extra[i]) push(extra[i]);
  }
  return out.slice(0, 5);
}

export function rankAhaResultsByPcsKeywords<
  T extends { id: number; fyear: number | null },
>(results: T[], keywords: string[]): T[] {
  const seen: Record<number, true> = {};
  const unique: T[] = [];
  for (let i = 0; i < results.length; i += 1) {
    const item = results[i];
    if (!item || seen[item.id]) continue;
    seen[item.id] = true;
    unique.push(item);
  }
  const needles = keywords.map((word) => word.toLowerCase()).filter(Boolean);
  const scoreOf = (item: T): number => {
    const rec = item as T & {
      keywords?: string | null;
      codingClinicOverview?: string | null;
      codingClinicQa?: string | null;
    };
    const blob = [
      rec.codingClinicOverview || "",
      rec.keywords || "",
      rec.codingClinicQa || "",
    ]
      .join(" ")
      .toLowerCase();
    let score = 0;
    for (let i = 0; i < needles.length; i += 1) {
      const needle = needles[i];
      if (!needle) continue;
      if (blob.indexOf(needle) >= 0) {
        score += Math.max(8 - i * 2, 2);
      }
    }
    return score;
  };
  return unique.sort((left: T, right: T) => {
    const delta = scoreOf(right) - scoreOf(left);
    if (delta !== 0) return delta;
    return (right.fyear ?? 0) - (left.fyear ?? 0) || left.id - right.id;
  });
}
